import { getJobs } from "@stryker/core/api/jobs";
import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { CompositeJob, MechanicalJob } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CopyIcon from "@/assets/icons/copy.svg?react";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import EmptyState from "@/components/EmptyState";
import DeleteJobModal from "@/components/job/DeleteJobModal";
import DuplicateJobModal from "@/components/job/DuplicateJobModal";
import NoCreditsModal from "@/components/job/NoCreditsModal";
import Status from "@/components/job/Status";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useOrganization from "@/hooks/useOrganization";
import { cn } from "@/lib/utils";

const columnHelper = createColumnHelper<CompositeJob | MechanicalJob>();

type JobsTableProps = {
  search: string;
  jobType?: "composite" | "mechanical" | "all";
  onCreateJob: () => void;
};

export default function JobsTable({ search, jobType, onCreateJob }: JobsTableProps) {
  const [showNoCredits, setShowNoCredits] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: canDeleteJob } = useQuery({
    ...profileQueryOptions(),
    select: (profile) => profileSelectors.isAdmin(profile) || profileSelectors.isEngineer(profile),
  });
  const {
    data: jobs,
    isFetching,
    error,
  } = useQuery({
    queryKey: [QueryKeys.JobsList, currentPage, search, jobType ?? "all"],
    queryFn: () =>
      getJobs({
        jobNumber: search,
        page: currentPage,
        jobType: jobType === "all" ? undefined : (jobType as "composite" | "mechanical"),
      }),
    staleTime: 10000,
    select: (response) => {
      return {
        ...response,
        Pagination: { ...response.Pagination, currentPage: Number.parseInt(response.Pagination.currentPage, 10) },
      };
    },
  });
  const { data: organization } = useOrganization();
  const hasCredits = !!organization?.balanceValue;

  useEffect(() => {
    setCurrentPage(1);
  }, [search, jobType]);

  const navigate = useNavigate();

  const [jobIdToRemove, setJobIdToRemove] = useState<null | number>(null);
  const [jobIdToDuplicate, setJobIdToDuplicate] = useState<null | number>(null);

  const totalPages = jobs?.Pagination?.totalPages ?? 0;
  const pages = Array.apply(null, Array(totalPages))
    .map((x, i) => i + 1)
    .reduce((acc: (number | string)[], page) => {
      const newAcc: (number | string)[] = [...acc];
      if (totalPages <= 7) {
        newAcc.push(page);
      } else if (currentPage === 1 && page - currentPage <= 2) {
        // handle first page
        newAcc.push(page);
      } else if (page - currentPage <= 1 && page - currentPage >= -1) {
        // handle pages around current
        newAcc.push(page);
      } else if (totalPages - page <= 2) {
        // handle three last pages
        newAcc.push(page);
      } else if (totalPages - page <= 6 && totalPages - currentPage <= 4) {
        // handle if current is in the end
        newAcc.push(page);
      }
      if (
        newAcc.length === 6 &&
        !newAcc.includes("...") &&
        typeof newAcc[3] === "number" &&
        typeof newAcc[2] === "number" &&
        newAcc[3] - newAcc[2] > 1
      ) {
        newAcc.splice(3, 0, "...");
      }
      return newAcc;
    }, []);

  const { repairSystemOptions } = useConfigOptions();

  const handleDuplicate = (id: number) => {
    if (hasCredits) {
      setJobIdToDuplicate(id);
    } else {
      setShowNoCredits(true);
    }
  };

  const repairTypeLabels: Record<string, string> = {
    straight_line: "Straight Line",
    tee: "Tee",
    long_radius: "Long Radius",
    flange_clamp: "Flange Clamp",
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("job_number", {
        header: "Job Number",
        cell: (info) => {
          return (
            <div className="flex items-center gap-2">
              <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
                {info.getValue()}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor("date_run", {
        header: "Date Run",
        cell: (info) => {
          const d = info.getValue();
          return (
            <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
              {d ? format(new Date(d), "MM.dd.yyyy") : "N/A"}
            </Typography>
          );
        },
      }),
      columnHelper.accessor("plant_name", {
        header: "Plant Name",
        cell: (info) => (
          <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
            {info.getValue()}
          </Typography>
        ),
      }),
      ...(jobType === "all" || jobType === "composite"
        ? [
            columnHelper.accessor("repair_system", {
              header: "Repair System",
              cell: (info) => {
                const repairSystem = repairSystemOptions.find((r) => r.value === info.getValue())?.title ?? "N/A";
                return (
                  <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
                    {repairSystem}
                  </Typography>
                );
              },
            }),
          ]
        : []),
      ...(jobType === "all" || jobType === "mechanical"
        ? [
            columnHelper.accessor("repair_type", {
              header: "Repair Type",
              cell: (info) => {
                const job = info.row.original as MechanicalJob;
                const repairType = job.repair_type ? (repairTypeLabels[job.repair_type] ?? "N/A") : "N/A";
                return (
                  <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
                    {repairType}
                  </Typography>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor("design_pressure", {
        header: "Design Pressure (psi)",
        cell: (info) => (
          <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor("design_temperature", {
        header: "Design Temperature",
        cell: (info) => (
          <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor("job_status", {
        header: "Job Status",
        cell: (info) => {
          const status = info.getValue();
          return status === null ? "N/A" : <Status status={status} />;
        },
      }),

      columnHelper.accessor("id", {
        header: "Action",
        cell: (info) => {
          return (
            <div className="flex justify-center gap-2">
              <CopyIcon
                className="cursor-pointer"
                onClick={() => {
                  handleDuplicate(info.getValue());
                }}
              />
              {canDeleteJob && (
                <DeleteIcon
                  className="cursor-pointer"
                  onClick={() => {
                    setJobIdToRemove(info.getValue());
                  }}
                />
              )}
            </div>
          );
        },
      }),
    ],
    [repairSystemOptions, jobType, canDeleteJob]
  );

  const table = useReactTable({
    data: jobs?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages;

  // Show error message instead of table when API fails
  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-8">
        <Typography variant="text-lg" weight="semibold" className="mb-2 text-red-600">
          Failed to load jobs
        </Typography>
        <Typography variant="text-small" className="text-gray-600">
          There was an error loading the jobs list. Please try again later.
        </Typography>
      </div>
    );
  }

  return (
    <>
      <table className="w-full border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="bg-gray-50 py-4 px-2 text-left">
                  {header.isPlaceholder ? null : (
                    <Typography className="text-center text-gray-600" variant="text-small" weight="medium">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Typography>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan={8}>
                <div className="mt-2 flex justify-center">
                  <Spinner svgClassName="h-16 w-16" />
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              return (
                <Fragment key={row.id}>
                  <tr className="border-y border-solid border-y-gray-200">
                    {row.getVisibleCells().map((cell) => {
                      const isFlagCell = cell.id.includes("flag");
                      const isNotActionCell = !cell.id.includes("id") && !isFlagCell;
                      return (
                        <td
                          className={cn(
                            "py-4 px-2 align-middle",
                            isNotActionCell && "cursor-pointer",
                            isFlagCell && "w-28"
                          )}
                          key={cell.id}
                          onClick={() => {
                            if (isNotActionCell) {
                              navigate(`/jobs/${cell.row.original.id}`);
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      <Pagination
        pages={pages}
        setCurrentPage={setCurrentPage}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        currentPage={currentPage}
        count={jobs?.Pagination.count ?? 0}
      />
      {jobs?.Pagination.count === 0 && <EmptyState onSubmit={onCreateJob} />}
      <DeleteJobModal jobIdToRemove={jobIdToRemove} setJobIdToRemove={setJobIdToRemove} />
      <DuplicateJobModal
        jobIdToDuplicate={jobIdToDuplicate}
        setJobIdToDuplicate={setJobIdToDuplicate}
        job={jobs?.results?.find((job) => job.id === jobIdToDuplicate) || null}
      />
      <NoCreditsModal isOpen={showNoCredits} setIsOpen={setShowNoCredits} />
    </>
  );
}
